import { useState, useEffect, useRef } from 'react';
import { Sparkles, Loader2, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { markAssignmentComplete } from '../services/assignmentService';
import type { AssignmentContext } from '../services/assignmentService';
import {
  getAppliedQuestions,
  generateAppliedQuestions,
  pollAppliedQuestions,
  getAppliedAttempts,
  saveAppliedAttempt,
  type AppliedQuestion,
  type AppliedDifficulty,
  type AppliedAttempt,
} from '../services/conceptPlus1Service';
import { logMasteryEvent } from '../services/masteryService';
import { summarizeAppliedPractice } from '../utils/appliedPracticeSummary';

// Hide, don't delete: the raw "N/M correct" score fraction stays in the file
// (flip this back to `true` to see it), just not rendered by default —
// replaced with a qualitative summary (summarizeAppliedPractice).
const SHOW_SCORE_FRACTION = false;

interface Props {
  conceptId: number;
  conceptName: string;
  subjectName: string;
  gradeLevel: number;
  studentId: string;
  conceptType?: string;
  assignmentContext?: AssignmentContext;
  onAllAnswered?: (difficulty: string) => void;
  onAwardBadge?: (criteria: string) => void;
}

const DIFFICULTIES: AppliedDifficulty[] = ['Basic', 'Standard', 'Advanced'];
const OPTION_KEYS = ['option_a', 'option_b', 'option_c', 'option_d'] as const;
const OPTION_LABELS = ['A', 'B', 'C', 'D'];

const DIFFICULTY_STYLES: Record<AppliedDifficulty, { tab: string; badge: string }> = {
  Basic: {
    tab: 'data-[active=true]:bg-green-600 data-[active=true]:text-white text-green-700 border-green-300',
    badge: 'bg-green-100 text-green-800',
  },
  Standard: {
    tab: 'data-[active=true]:bg-orange-500 data-[active=true]:text-white text-orange-700 border-orange-300',
    badge: 'bg-orange-100 text-orange-800',
  },
  Advanced: {
    tab: 'data-[active=true]:bg-purple-600 data-[active=true]:text-white text-purple-700 border-purple-300',
    badge: 'bg-purple-100 text-purple-800',
  },
};

interface QuestionCardProps {
  q: AppliedQuestion;
  index: number;
  attempt: AppliedAttempt | null;
  onAnswer: (optionIndex: number) => void;
  onMistakeCategory?: (category: string) => void;
}

function QuestionCard({ q, index, attempt, onAnswer, onMistakeCategory }: QuestionCardProps) {
  const answered = attempt !== null;
  const [mistakeCategory, setMistakeCategory] = useState<string | null>(null);
  const [glowing, setGlowing] = useState(false);

  function handleLocalAnswer(optionIndex: number) {
    if (optionIndex === q.correct_option_index) {
      setGlowing(true);
      setTimeout(() => setGlowing(false), 500);
    }
    onAnswer(optionIndex);
  }

  function optionStyle(optionIndex: number): string {
    const base = 'w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ';
    if (!answered) {
      return base + 'border-gray-200 hover:border-coral-warm hover:bg-orange-50 text-gray-700';
    }
    if (optionIndex === q.correct_option_index) {
      return base + `border-green-400 bg-green-50 text-green-800 font-medium${glowing ? ' correct-glow' : ''}`;
    }
    if (optionIndex === attempt!.option_chosen) {
      return base + 'border-red-400 bg-red-50 text-red-800';
    }
    return base + 'border-gray-200 text-gray-400';
  }

  const isWrong = answered && !attempt!.correct;
  const showSolution = !isWrong || mistakeCategory !== null;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 flex items-start gap-3">
        <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-orange-100 text-coral-warm text-xs font-bold flex items-center justify-center">
          {index + 1}
        </span>
        <div className="flex-1 space-y-3">
          <p className="text-sm text-gray-800 font-medium leading-snug">{q.question}</p>
          {q.context && (
            <p className="text-xs text-gray-500 italic">{q.context}</p>
          )}
          <div className="space-y-2">
            {OPTION_KEYS.map((key, i) => (
              <button
                key={i}
                disabled={answered}
                onClick={() => handleLocalAnswer(i)}
                className={optionStyle(i)}
              >
                <span className="font-semibold mr-2">{OPTION_LABELS[i]}.</span>
                {q[key]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {answered && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 bg-gray-50">
          <div className="flex items-center gap-2 pt-3">
            {attempt!.correct
              ? <CheckCircle2 size={15} className="text-green-600 flex-shrink-0" />
              : <AlertCircle size={15} className="text-red-500 flex-shrink-0" />}
            <span className={`text-xs font-semibold ${attempt!.correct ? 'text-green-700' : 'text-red-600'}`}>
              {attempt!.correct
                ? 'Correct!'
                : `Incorrect — correct answer is ${OPTION_LABELS[q.correct_option_index]}. In JEE, wrong applied questions cost marks.`}
            </span>
            {attempt!.time_taken_seconds != null && (
              <span className="ml-auto flex items-center gap-1 text-xs text-gray-400">
                <Clock size={12} /> {attempt!.time_taken_seconds}s
              </span>
            )}
          </div>

          {/* Wrong answer: self-categorisation gate (blocks solution until picked) */}
          {isWrong && !mistakeCategory && (
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-medium">Why did you get that wrong?</p>
              {["Silly mistake", "Didn't understand", "Had no idea"].map((cat) => (
                <button
                  key={cat}
                  onClick={() => { setMistakeCategory(cat); onMistakeCategory?.(cat); }}
                  className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Solution shown once self-cat done (or if correct) */}
          {showSolution && (
            <>
              <div className="space-y-1.5">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Solution</p>
                <ol className="space-y-1">
                  {q.solution_steps.map((step, i) => (
                    <li key={i} className="flex gap-2 text-sm text-gray-700">
                      <span className="flex-shrink-0 text-coral-warm font-medium">{i + 1}.</span>
                      <span>{step.replace(/^Step\s*\d+[:.]\s*/i, '')}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <CheckCircle2 size={15} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm font-medium text-green-800">{q.final_answer}</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function AppliedPracticeSection({ conceptId, conceptName, subjectName, gradeLevel, studentId, conceptType, assignmentContext, onAllAnswered, onAwardBadge }: Props) {
  const [activeDifficulty, setActiveDifficulty] = useState<AppliedDifficulty>('Basic');
  const [questionsByDifficulty, setQuestionsByDifficulty] = useState<Partial<Record<AppliedDifficulty, AppliedQuestion[]>>>({});
  const [attemptsByDifficulty, setAttemptsByDifficulty] = useState<Partial<Record<AppliedDifficulty, Record<number, AppliedAttempt>>>>({});
  const [loadingDifficulty, setLoadingDifficulty] = useState<AppliedDifficulty | null>(null);
  const [timedOut, setTimedOut] = useState<Partial<Record<AppliedDifficulty, boolean>>>({});
  const [consecutiveCorrectBasic, setConsecutiveCorrectBasic] = useState(0);
  const [showDifficultyNudge, setShowDifficultyNudge] = useState(false);
  const [retakeMode, setRetakeMode] = useState<Partial<Record<AppliedDifficulty, boolean>>>({});
  const [currentAttemptNumber, setCurrentAttemptNumber] = useState<Partial<Record<AppliedDifficulty, number>>>({});
  const stopPollRef = useRef<(() => void) | null>(null);
  const practiceStreak = useRef(0);
  const allAnsweredFired = useRef<Partial<Record<AppliedDifficulty, boolean>>>({});
  const assignmentNewCountRef = useRef(0);
  const assignmentCorrectRef = useRef(0);
  const assignmentFiredRef = useRef(false);
  // Per-difficulty "gap timer": reset whenever a question set becomes the one
  // being actively answered, so time_taken_seconds ~= how long since the last
  // answer (or since the tier was opened, for the first one).
  const lastEventAtRef = useRef<Partial<Record<AppliedDifficulty, number>>>({});

  useEffect(() => {
    DIFFICULTIES.forEach(async (d) => {
      const cached = await getAppliedQuestions(conceptId, d);
      // Only use cached questions that have MCQ options populated (old format has empty options)
      if (cached && cached.length > 0 && cached[0].option_a?.trim()) {
        setQuestionsByDifficulty((prev) => ({ ...prev, [d]: cached }));
        lastEventAtRef.current[d] = Date.now();
        if (studentId) loadAttempts(d);
      }
    });
  }, [conceptId]);

  useEffect(() => {
    return () => { stopPollRef.current?.(); };
  }, []);

  async function loadAttempts(difficulty: AppliedDifficulty) {
    if (!studentId) return;
    const attempts = await getAppliedAttempts(studentId, conceptId, difficulty);
    const map: Record<number, AppliedAttempt> = {};
    let maxAttemptNum = 0;
    attempts.forEach(a => {
      map[a.question_index] = a;
      if ((a.attempt_number ?? 1) > maxAttemptNum) maxAttemptNum = a.attempt_number ?? 1;
    });
    setAttemptsByDifficulty((prev) => ({ ...prev, [difficulty]: map }));
    if (maxAttemptNum > 0) {
      setCurrentAttemptNumber((prev) => ({ ...prev, [difficulty]: maxAttemptNum }));
    }
  }

  function handleRetake(difficulty: AppliedDifficulty) {
    setAttemptsByDifficulty((prev) => ({ ...prev, [difficulty]: {} }));
    setRetakeMode((prev) => ({ ...prev, [difficulty]: true }));
    allAnsweredFired.current[difficulty] = false;
    lastEventAtRef.current[difficulty] = Date.now();
  }

  async function handleAnswer(questionIndex: number, optionIndex: number) {
    if (!studentId) return;
    const questions = questionsByDifficulty[activeDifficulty];
    if (!questions) return;
    const q = questions[questionIndex];
    const correct = optionIndex === q.correct_option_index;

    const timeTakenSeconds = Math.round((Date.now() - (lastEventAtRef.current[activeDifficulty] ?? Date.now())) / 1000);
    lastEventAtRef.current[activeDifficulty] = Date.now();

    const attempt: AppliedAttempt = { question_index: questionIndex, option_chosen: optionIndex, correct, time_taken_seconds: timeTakenSeconds };
    setAttemptsByDifficulty((prev) => ({
      ...prev,
      [activeDifficulty]: { ...(prev[activeDifficulty] ?? {}), [questionIndex]: attempt },
    }));

    const result = await saveAppliedAttempt({
      studentId,
      conceptId,
      difficulty: activeDifficulty,
      questionIndex,
      optionChosen: optionIndex,
      correct,
      timeTakenSeconds,
    });

    setCurrentAttemptNumber((prev) => ({ ...prev, [activeDifficulty]: result.attemptNumber }));

    const now = new Date();
    logMasteryEvent({
      studentId,
      conceptId,
      eventType: 'applied_practice',
      correct,
      difficulty: activeDifficulty,
      interactionStartedAt: now,
      firstResponseAt: now,
      finalSubmissionAt: now,
    });

    if (result.isNew && assignmentContext && !assignmentFiredRef.current && studentId) {
      const countsForAssignment =
        assignmentContext.type === 'practice' ||
        (assignmentContext.type === 'challenge' && activeDifficulty === 'Advanced');
      if (countsForAssignment) {
        assignmentNewCountRef.current += 1;
        if (correct) assignmentCorrectRef.current += 1;
        if (assignmentNewCountRef.current >= 5) {
          assignmentFiredRef.current = true;
          const score = Math.round((assignmentCorrectRef.current / assignmentNewCountRef.current) * 100);
          markAssignmentComplete(assignmentContext.id, studentId, score, assignmentNewCountRef.current).catch(() => {});
        }
      }
    }

    if (activeDifficulty === 'Basic') {
      if (correct) {
        const next = consecutiveCorrectBasic + 1;
        setConsecutiveCorrectBasic(next);
        if (next >= 3) setShowDifficultyNudge(true);
      } else {
        setConsecutiveCorrectBasic(0);
      }
    }

    // Badge: first correct practice answer
    if (correct) {
      onAwardBadge?.('practice_first_correct');
      practiceStreak.current += 1;
      if (practiceStreak.current >= 3) onAwardBadge?.('correct_streak_3');
    } else {
      practiceStreak.current = 0;
    }

    // Fire onAllAnswered when every question in this difficulty is answered
    if (!allAnsweredFired.current[activeDifficulty]) {
      const newAttempts = { ...(attemptsByDifficulty[activeDifficulty] ?? {}), [questionIndex]: attempt };
      const allDone = questions.length > 0 && questions.every((_, idx) => newAttempts[idx] !== undefined);
      if (allDone) {
        allAnsweredFired.current[activeDifficulty] = true;
        onAllAnswered?.(activeDifficulty);
      }
    }
  }

  function handleMistakeCategory(category: string) {
    if (!studentId) return;
    logMasteryEvent({
      studentId,
      conceptId,
      eventType: 'applied_practice',
      correct: false,
      mistakeCategory: category,
      difficulty: activeDifficulty,
    });
  }

  async function handleGenerate(difficulty: AppliedDifficulty) {
    if (loadingDifficulty) return;
    setLoadingDifficulty(difficulty);
    setTimedOut((prev) => ({ ...prev, [difficulty]: false }));

    const result = await generateAppliedQuestions({
      conceptId,
      conceptName,
      subject: subjectName,
      difficulty,
      gradeLevel,
      conceptType,
    });

    if (result.status === 'ready' && result.questions) {
      setQuestionsByDifficulty((prev) => ({ ...prev, [difficulty]: result.questions! }));
      lastEventAtRef.current[difficulty] = Date.now();
      setLoadingDifficulty(null);
      if (studentId) loadAttempts(difficulty);
      return;
    }

    if (result.status === 'generating') {
      stopPollRef.current = pollAppliedQuestions(
        conceptId,
        difficulty,
        (questions) => {
          setQuestionsByDifficulty((prev) => ({ ...prev, [difficulty]: questions }));
          lastEventAtRef.current[difficulty] = Date.now();
          setLoadingDifficulty(null);
          if (studentId) loadAttempts(difficulty);
        },
        () => {
          setTimedOut((prev) => ({ ...prev, [difficulty]: true }));
          setLoadingDifficulty(null);
        }
      );
      return;
    }

    setLoadingDifficulty(null);
  }

  function handleTabChange(d: AppliedDifficulty) {
    setActiveDifficulty(d);
    lastEventAtRef.current[d] = Date.now();
    if (studentId && questionsByDifficulty[d] && !attemptsByDifficulty[d]) {
      loadAttempts(d);
    }
  }

  const currentQuestions = questionsByDifficulty[activeDifficulty];
  const currentAttempts = attemptsByDifficulty[activeDifficulty] ?? {};
  const isLoading = loadingDifficulty === activeDifficulty;
  const isTimedOut = timedOut[activeDifficulty];
  const attemptNum = currentAttemptNumber[activeDifficulty] ?? 0;
  const isRetaking = retakeMode[activeDifficulty] ?? false;
  const allAnswered = currentQuestions
    ? currentQuestions.length > 0 && currentQuestions.every((_, idx) => currentAttempts[idx] !== undefined)
    : false;
  const correctCount = Object.values(currentAttempts).filter(a => a.correct).length;
  const totalTimeSeconds = Object.values(currentAttempts).reduce((s, a) => s + (a.time_taken_seconds ?? 0), 0);
  const practiceSummary = currentQuestions ? summarizeAppliedPractice(currentQuestions, currentAttempts) : { headline: "Practice complete" };

  return (
    <section className="mt-6 rounded-2xl border border-orange-100 bg-white shadow-sm overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
        <Sparkles size={18} className="text-coral-warm flex-shrink-0" />
        <div>
          <h3 className="text-base font-semibold text-gray-900">Applied Practice</h3>
          <p className="text-xs text-gray-500">Exam-style application questions — answer first, then see the solution</p>
        </div>
      </div>

      <div className="flex gap-2 px-5 pt-4">
        {DIFFICULTIES.map((d) => (
          <button
            key={d}
            data-active={activeDifficulty === d}
            onClick={() => handleTabChange(d)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${DIFFICULTY_STYLES[d].tab}`}
          >
            {d}
            {questionsByDifficulty[d] && (
              <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${DIFFICULTY_STYLES[d].badge}`}>
                {questionsByDifficulty[d]!.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="px-5 py-4">
        {isLoading ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <Loader2 size={28} className="text-orange-300 animate-spin" />
            <p className="text-sm text-gray-500">AI is preparing {activeDifficulty.toLowerCase()} questions…</p>
          </div>
        ) : isTimedOut ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <AlertCircle size={24} className="text-amber-400" />
            <p className="text-sm text-gray-600">Generation timed out. Please try again.</p>
            <button
              onClick={() => handleGenerate(activeDifficulty)}
              className="px-4 py-2 rounded-lg bg-coral-warm text-white text-sm font-medium hover:bg-orange-600 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : currentQuestions ? (
          <div className="space-y-3">
            {isRetaking && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-orange-50 border border-orange-200">
                <span className="text-xs font-semibold text-coral-warm">
                  Retake{attemptNum > 0 ? ` — Attempt ${attemptNum + 1}` : ''} — try to beat your last score
                </span>
              </div>
            )}
            {showDifficultyNudge && activeDifficulty === 'Basic' && (
              <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-300">
                <p className="text-sm text-green-800 font-medium">You've got Basic right 3 times. Try Standard?</p>
                <button
                  onClick={() => { setShowDifficultyNudge(false); handleTabChange('Standard'); }}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors"
                >
                  Switch →
                </button>
              </div>
            )}
            {currentQuestions.map((q, i) => (
              <QuestionCard
                key={i}
                q={q}
                index={i}
                attempt={currentAttempts[i] ?? null}
                onAnswer={(optionIndex) => handleAnswer(i, optionIndex)}
                onMistakeCategory={handleMistakeCategory}
              />
            ))}
            {allAnswered && !isRetaking && (
              <div className="mt-2 rounded-xl border border-orange-200 bg-gradient-to-br from-indigo-50 to-white p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold text-coral-warm uppercase tracking-wide mb-1">Practice complete</p>
                    {SHOW_SCORE_FRACTION ? (
                      <p className="text-2xl font-bold text-ocean-deep">
                        {correctCount}
                        <span className="text-base font-normal text-coral-warm">/{currentQuestions!.length} correct</span>
                      </p>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-ocean-deep">{practiceSummary.headline}</p>
                        {practiceSummary.detail && (
                          <p className="text-sm font-medium text-coral-warm mt-0.5">{practiceSummary.detail}</p>
                        )}
                      </>
                    )}
                    <p className="flex items-center gap-1 text-xs text-orange-300 mt-1">
                      <Clock size={12} /> {totalTimeSeconds < 60 ? `${totalTimeSeconds}s` : `${Math.floor(totalTimeSeconds / 60)}m ${totalTimeSeconds % 60}s`} total
                    </p>
                  </div>
                  <button
                    onClick={() => handleRetake(activeDifficulty)}
                    className="px-5 py-2.5 rounded-xl bg-coral-warm text-white text-sm font-semibold hover:bg-orange-600 active:scale-95 transition-all shadow-sm"
                  >
                    Retake
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center">
              <Sparkles size={22} className="text-orange-300" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">{activeDifficulty} applied questions</p>
              <p className="text-xs text-gray-500 mt-1">
                Exam-style scenarios for "{conceptName}"
              </p>
            </div>
            <button
              onClick={() => handleGenerate(activeDifficulty)}
              disabled={!!loadingDifficulty}
              className="px-5 py-2 rounded-lg bg-coral-warm text-white text-sm font-medium hover:bg-orange-600 disabled:opacity-50 transition-colors"
            >
              Generate Questions
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
